// frontend/src/components/GoogleMapsDistance.jsx
import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Loader2, Search, Locate, Map } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GoogleMapsDistance = ({ onDistanceChange, onAddressResolved, externalSearchQuery, externalSearchTrigger, initialLocation, onLocationSelect }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [address, setAddress] = useState('');
  const [distance, setDistance] = useState(null);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [deliveryAvailable, setDeliveryAvailable] = useState(true); // Add this state
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const [isValidAddress, setIsValidAddress] = useState(false);
  const [resolvedPlace, setResolvedPlace] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null); // Added missing state
  const [mapLoaded, setMapLoaded] = useState(false);
  const [nearestBranch, setNearestBranch] = useState(null);
  
  const inputRef = useRef(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  
  const BRANCH_LOCATIONS = [
    {
      id: 'amerat',
      name: 'WOW! PIZZA - Al Amarat, Phase 5',
      lat: 23.475414,
      lng: 58.481735
    },
    {
      id: 'qurum',
      name: 'WOW! PIZZA - Qurum',
      lat: 23.615725,
      lng: 58.496988
    },
    {
      id: 'khuwair',
      name: 'WOW! PIZZA - Al Khuwair',
      lat: 23.586524,
      lng: 58.427727
    }
  ];

  const DEFAULT_MAP_CENTER = { lat: 23.5455695, lng: 58.4893615 };

  const normalizeText = (value) => (value || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();

  const getAddressMatchScore = (result, expectedWayNumber, expectedBuildingNumber) => {
    const formatted = normalizeText(result?.formatted_address);
    const components = result?.address_components || [];
    const componentText = normalizeText(
      components.map((component) => `${component.long_name} ${component.short_name}`).join(' ')
    );
    const fullText = `${formatted} ${componentText}`;

    const way = normalizeText(expectedWayNumber);
    const building = normalizeText(expectedBuildingNumber);
    let score = 0;

    if (way) {
      if (fullText.includes(`${way} way`) || fullText.includes(`way ${way}`) || fullText.includes(`${way} طريق`) || fullText.includes(`طريق ${way}`)) score += 4;
      if (new RegExp(`(^|\\D)${way}(\\D|$)`).test(fullText)) score += 2;
    }

    if (building) {
      if (fullText.includes(`building ${building}`) || fullText.includes(`${building} building`) || fullText.includes(`${building} مبنى`) || fullText.includes(`مبنى ${building}`)) score += 4;
      if (new RegExp(`(^|\\D)${building}(\\D|$)`).test(fullText)) score += 2;
    }

    return score;
  };

  // Generate session token for better autocomplete quality
  useEffect(() => {
    setSessionToken(Math.random().toString(36).substring(2, 15));
  }, []);

  // Initialize map function (moved outside useEffect for accessibility)
  const initializeMap = () => {
    try {
      // Check if map container exists
      if (!mapRef.current) {
        console.log('Map container not found, retrying...');
        setTimeout(initializeMap, 100);
        return;
      }

      // Check if map is already initialized
      if (mapInstanceRef.current) {
        return;
      }

      // Check if Google Maps is loaded
      if (!window.google || !window.google.maps) {
        console.log('Google Maps not loaded yet, retrying...');
        setTimeout(initializeMap, 100);
        return;
      }

      // Initialize map
      const map = new window.google.maps.Map(mapRef.current, {
        center: DEFAULT_MAP_CENTER,
        zoom: 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });

      mapInstanceRef.current = map;

      // Add branch markers
      BRANCH_LOCATIONS.forEach((branch) => {
        new window.google.maps.Marker({
          position: { lat: branch.lat, lng: branch.lng },
          map,
          title: branch.name,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });
      });

      // Initialize autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'om' },
          fields: ['geometry', 'formatted_address', 'address_components']
        }
      );

      // Handle place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (!place.geometry) {
          setError('No location found for this address');
          return;
        }

        const location = place.geometry.location;
        setSelectedLocation(location);
        setAddress(place.formatted_address);
        setIsValidAddress(true);

        // Auto-fill address details
        autoFillAddressDetails(place);

        // Update map focus and marker for selected place
        map.setCenter(location);
        map.setZoom(16);
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        markerRef.current = new window.google.maps.Marker({
          position: location,
          map,
          title: 'Your Location',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });

        // Calculate distance
        calculateDistanceFromLocation(location);
      });

      // Add click listener to map for manual marker placement
      map.addListener('click', (event) => {
        const clickedLocation = event.latLng;
        
        // Remove existing marker
        if (markerRef.current) {
          markerRef.current.setMap(null);
        }
        
        // Add new marker at clicked location
        markerRef.current = new window.google.maps.Marker({
          position: clickedLocation,
          map: map,
          title: 'Your Selected Location',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });
        
        // Update state
        setSelectedLocation(clickedLocation);
        
        // Show success message
        setError('');
        
        // Reverse geocode to get address
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: clickedLocation }, (results, status) => {
          if (status === 'OK' && results[0]) {
            setAddress(results[0].formatted_address);
            setIsValidAddress(true);
            autoFillAddressDetails(results[0]);
          }
        });
        
        // Calculate distance
        calculateDistanceFromLocation(clickedLocation);
      });

      // Trigger resize event to ensure map renders properly
      setTimeout(() => {
        if (window.google && mapInstanceRef.current) {
          window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
        }
      }, 100);

    } catch (error) {
      console.error('Error initializing map:', error);
      setError('Failed to initialize map. Please refresh the page.');
    }
  };

  // Load Google Maps script
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps) {
        setMapLoaded(true);
        // Force map initialization after script loads
        setTimeout(() => {
          if (mapRef.current && !mapInstanceRef.current) {
            initializeMap();
          }
        }, 100);
        return;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        setMapLoaded(true);
        // Force map initialization after script loads
        setTimeout(() => {
          if (mapRef.current && !mapInstanceRef.current) {
            initializeMap();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&callback=initGoogleMaps`;
      script.async = true;
      script.defer = true;
      
      window.initGoogleMaps = () => {
        setMapLoaded(true);
        // Force map initialization after script loads
        setTimeout(() => {
          if (mapRef.current && !mapInstanceRef.current) {
            initializeMap();
          }
        }, 100);
      };
      
      script.onerror = () => {
        setError('Failed to load Google Maps');
        console.error('Google Maps script failed to load');
      };
      
      document.head.appendChild(script);
    };

    if (process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      loadGoogleMapsScript();
    } else {
      setError('Google Maps API key not found');
    }
  }, []);

  // Force map initialization when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current && !mapInstanceRef.current && window.google) {
        console.log('Forcing map initialization...');
        initializeMap();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Restore saved location pin after map loads
  useEffect(() => {
    if (!initialLocation || !mapLoaded) return;

    let timerId;
    let retries = 0;
    const MAX_RETRIES = 20; // max 4 seconds of retries (20 × 200ms)

    const restorePin = () => {
      if (!mapInstanceRef.current || !window.google) {
        if (retries < MAX_RETRIES) {
          retries++;
          timerId = setTimeout(restorePin, 200);
        }
        return;
      }
      const loc = new window.google.maps.LatLng(initialLocation.lat, initialLocation.lng);
      setSelectedLocation(loc);
      if (initialLocation.address) {
        setAddress(initialLocation.address);
        setIsValidAddress(true);
      }
      mapInstanceRef.current.setCenter(loc);
      mapInstanceRef.current.setZoom(16);
      if (markerRef.current) markerRef.current.setMap(null);
      markerRef.current = new window.google.maps.Marker({
        position: loc,
        map: mapInstanceRef.current,
        title: 'Your Location',
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          scaledSize: new window.google.maps.Size(32, 32)
        }
      });
      calculateDistanceFromLocation(loc);
    };

    timerId = setTimeout(restorePin, 700);
    return () => clearTimeout(timerId);
  }, [mapLoaded]); // only re-run when map finishes loading

  // Notify parent when a location+address is fully resolved
  useEffect(() => {
    if (!selectedLocation || !address || !isValidAddress || !onLocationSelect) return;
    const lat = typeof selectedLocation.lat === 'function' ? selectedLocation.lat() : selectedLocation.lat;
    const lng = typeof selectedLocation.lng === 'function' ? selectedLocation.lng() : selectedLocation.lng;
    onLocationSelect(lat, lng, address);
  }, [selectedLocation, address, isValidAddress]); // onLocationSelect is stable

  const calculateDeliveryCharge = (dist, branch = nearestBranch, meta = {}) => {
    let charge;
    let available = true;
    
    // Keep in sync with calculate_delivery_charge in backend/server.py
    if (dist <= 5) {
      charge = 0;
    } else if (dist <= 10) {
      charge = 0.5;
    } else if (dist <= 15) {
      charge = 1.0;
    } else if (dist <= 20) {
      charge = 1.5;
    } else {
      charge = 0;
      available = false; // Delivery not available
    }

    setDeliveryCharge(charge);
    setDeliveryAvailable(available);
    onDistanceChange(dist, charge, available, branch, meta); // Pass availability and nearest branch to parent
  };

  // Get user's current location
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          resolve(location);
        },
        (error) => {
          reject(new Error(`Geolocation error: ${error.message}`));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  };

  // Locate Me functionality
  const handleLocateMe = async () => {
    setLocating(true);
    setError('');
    
    try {
      const location = await getUserLocation();
      setUserLocation(location);
      
      // Create Google Maps LatLng object
      const googleLocation = new window.google.maps.LatLng(location.lat, location.lng);
      setSelectedLocation(googleLocation);
      
      // Ensure map is initialized
      if (!mapInstanceRef.current) {
        setError('Map not initialized. Please wait a moment and try again.');
        return;
      }
      
      // Update map smoothly
      mapInstanceRef.current.panTo(googleLocation);
      mapInstanceRef.current.setZoom(18);
      
      // Remove existing marker first
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      
      // Add new marker
      markerRef.current = new window.google.maps.Marker({
        position: googleLocation,
        map: mapInstanceRef.current,
        title: 'Your Location',
        icon: {
          url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
          scaledSize: new window.google.maps.Size(32, 32)
        }
      });
      
      // Reverse geocode to get address
      const geocoder = new window.google.maps.Geocoder();
      const result = await new Promise((resolve, reject) => {
        geocoder.geocode({ location: googleLocation }, (results, status) => {
          if (status === 'OK' && results[0]) {
            resolve(results[0]);
          } else {
            reject(new Error('Failed to get address from location'));
          }
        });
      });
      
      // Set address
      setAddress(result.formatted_address);
      setIsValidAddress(true);
      
      // Auto-fill address details
      autoFillAddressDetails(result);
      
      // Calculate distance
      await calculateDistanceFromLocation(googleLocation);
      
    } catch (error) {
      setError(`Location error: ${error.message}`);
      console.error('Location error:', error);
    } finally {
      setLocating(false);
    }
  };

  // Auto-fill address details from geocoding result
  const autoFillAddressDetails = (geocodingResult) => {
    const addressComponents = geocodingResult.address_components;
    const autoFilledData = {
      wayNumber: '',
      buildingNumber: '',
      flatNumber: '', // Leave empty for user to fill
      floor: '', // Leave empty for user to fill
      additionalInfo: ''
    };
    
    // Extract way number and building number from address components
    addressComponents.forEach(component => {
      const types = component.types;
      
      // Look for street number (way number)
      if (types.includes('street_number')) {
        autoFilledData.wayNumber = component.long_name;
      }
      
      // Look for route (street name)
      if (types.includes('route')) {
        // Extract building number from route if possible
        const routeMatch = component.long_name.match(/Building\s+(\d+)/i);
        if (routeMatch) {
          autoFilledData.buildingNumber = routeMatch[1];
        }
      }
      
      // Look for sublocality (area name)
      if (types.includes('sublocality')) {
        autoFilledData.additionalInfo = component.long_name;
      }
    });
    
    // Call the callback to update parent component
    if (onAddressResolved) {
      onAddressResolved(autoFilledData);
    }
  };

  // Calculate distance from location
  const calculateDistanceFromLocation = async (userLocation, meta = {}) => {
    setLoading(true);
    setError('');

    try {
      const service = new window.google.maps.DistanceMatrixService();
      
      service.getDistanceMatrix(
        {
          origins: BRANCH_LOCATIONS.map((branch) => ({ lat: branch.lat, lng: branch.lng })),
          destinations: [userLocation],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.METRIC
        },
        (response, status) => {
          if (status === 'OK' && response.rows?.length) {
            let nearestBranchIndex = -1;
            let shortestDistanceKm = null;

            response.rows.forEach((row, index) => {
              const element = row?.elements?.[0];
              if (!element || element.status !== 'OK' || !element.distance?.value) {
                return;
              }

              const distanceKm = element.distance.value / 1000;
              if (shortestDistanceKm === null || distanceKm < shortestDistanceKm) {
                shortestDistanceKm = distanceKm;
                nearestBranchIndex = index;
              }
            });

            if (nearestBranchIndex === -1 || shortestDistanceKm === null) {
              setError('Could not calculate distance');
              setDistance(null);
              setNearestBranch(null);
              calculateDeliveryCharge(0);
              setLoading(false);
              return;
            }

            const nearest = BRANCH_LOCATIONS[nearestBranchIndex];
            const distanceKm = shortestDistanceKm;
            setDistance(distanceKm);
            setNearestBranch(nearest);
            calculateDeliveryCharge(distanceKm, nearest, meta);
          } else {
            setError('Could not calculate distance');
            setDistance(null);
            setNearestBranch(null);
            calculateDeliveryCharge(0);
          }
          setLoading(false);
        }
      );
    } catch (err) {
      setError('Distance calculation failed');
      setDistance(null);
      setNearestBranch(null);
      calculateDeliveryCharge(0);
      setLoading(false);
    }
  };

  // Enhanced address search with resolution
  const searchExactAddress = async (searchQuery = address, options = {}) => {
    const normalizedQuery = (searchQuery || '').trim();
    if (!normalizedQuery) {
      setError('Please enter an address to search.');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const expectedWayNumber = options.expectedWayNumber || '';
      const expectedBuildingNumber = options.expectedBuildingNumber || '';

      // Use Google Maps Geocoding service directly
      const geocoder = new window.google.maps.Geocoder();
      const muscatBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(23.2, 58.2),
        new window.google.maps.LatLng(23.9, 58.8)
      );
      const candidateQueries = [
        normalizedQuery,
        normalizedQuery.replace(/way\s+(\d+)/i, '$1 way'),
        normalizedQuery.replace(/building\s+(\d+)/i, '$1 building'),
        normalizedQuery.replace(/building\s+(\d+).*way\s+(\d+)/i, 'Building $1, $2 Way, Muscat, Oman'),
        normalizedQuery.replace(/way\s+(\d+).*building\s+(\d+)/i, '$1 Way, Building $2, Muscat, Oman'),
        `${normalizedQuery}, Muscat`,
        `${normalizedQuery}, Muscat, Oman`,
        normalizedQuery.replace(/,/g, ' ')
      ];

      const allResults = [];
      for (const candidate of candidateQueries) {
        // eslint-disable-next-line no-await-in-loop
        const geocodeResults = await new Promise((resolve) => {
          geocoder.geocode(
            {
              address: candidate,
              componentRestrictions: { country: 'om' },
              region: 'om',
              bounds: muscatBounds
            },
            (results, status) => {
              if (status === 'OK' && results?.length) {
                resolve(results);
              } else {
                resolve([]);
              }
            }
          );
        });
        allResults.push(...geocodeResults);
      }

      const uniqueResults = allResults.filter((result, index, arr) => {
        const key = result.place_id || result.formatted_address;
        return arr.findIndex((item) => (item.place_id || item.formatted_address) === key) === index;
      });

      const rankedResults = uniqueResults
        .map((result) => ({
          result,
          score: getAddressMatchScore(result, expectedWayNumber, expectedBuildingNumber)
        }))
        .sort((a, b) => b.score - a.score);

      const best = rankedResults[0];
      const result = best?.result || null;

      if (!result) {
        throw new Error('No address found');
      }

      const location = result.geometry.location;
      setSelectedLocation(location);
      setAddress(result.formatted_address);
      setIsValidAddress(true);
      
      // Auto-fill address details
      autoFillAddressDetails(result);
      
      // Update map
      if (mapInstanceRef.current) {
        mapInstanceRef.current.setCenter(location);
        mapInstanceRef.current.setZoom(18);
      }
      
      // Add marker
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      
      if (mapInstanceRef.current) {
        markerRef.current = new window.google.maps.Marker({
          position: location,
          map: mapInstanceRef.current,
          title: 'Your Location',
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
            scaledSize: new window.google.maps.Size(32, 32)
          }
        });
      }
      
      // Calculate distance
      await calculateDistanceFromLocation(location, {
        sourceQuery: normalizedQuery,
        resolvedAddress: result.formatted_address
      });
      
    } catch (error) {
      setError('Failed to search address. Please try again.');
      console.error('Address search error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!externalSearchTrigger) {
      return;
    }

    if (externalSearchQuery && externalSearchQuery.trim()) {
      setAddress(externalSearchQuery);
      searchExactAddress(externalSearchQuery);
    }
  }, [externalSearchTrigger, externalSearchQuery]);

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="bg-red-50 p-4 rounded-lg mb-4">
        <div className="text-red-600 text-sm">
          Google Maps API key not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 p-4 rounded-lg mb-4">
      <div className="flex items-center mb-3">
        <MapPin className="h-5 w-5 text-blue-600 mr-2" />
        <h4 className="font-semibold text-blue-800">{t('checkout.deliveryFromNearest')}</h4>
      </div>

      {/* Always show price range */}
      <div className="mb-4 p-3 bg-white rounded-lg border border-blue-200">
        <h5 className="font-semibold text-blue-800 mb-2">{t('checkout.deliveryCharges')}</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
          <div className="text-center p-2 bg-green-50 rounded border">
            <div className="font-semibold text-green-800">0-5 km</div>
            <div className="text-green-600">0 OMR</div>
          </div>
          <div className="text-center p-2 bg-blue-50 rounded border">
            <div className="font-semibold text-blue-800">6-10 km</div>
            <div className="text-blue-600">0.5 OMR</div>
          </div>
          <div className="text-center p-2 bg-yellow-50 rounded border">
            <div className="font-semibold text-yellow-800">11-15 km</div>
            <div className="text-yellow-600">1.0 OMR</div>
          </div>
          <div className="text-center p-2 bg-orange-50 rounded border">
            <div className="font-semibold text-orange-800">16-20 km</div>
            <div className="text-orange-600">1.5 OMR</div>
          </div>
        </div>
        <div className="mt-2 text-xs text-red-600 text-center">
          {t('checkout.deliveryBeyond20km')}
        </div>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <button
            onClick={handleLocateMe}
            disabled={locating}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 flex items-center justify-center space-x-2 text-base font-medium"
          >
            {locating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('checkout.locating')}</span>
              </>
            ) : (
              <>
                <Navigation className="h-4 w-4" />
                <span>{t('checkout.useMyLocation')}</span>
              </>
            )}
          </button>

          <button
            onClick={() => searchExactAddress(address)}
            disabled={!address.trim() || loading}
            title={!address.trim() ? 'Type an address first' : ''}
            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:opacity-50 text-base font-medium"
          >
            {t('checkout.searchAddress')}
          </button>
      </div>

      {/* Address Input */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('checkout.enterYourAddress')}
        </label>
        <input
          ref={inputRef}
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              searchExactAddress(address);
            }
          }}
          placeholder={t('checkout.typeAddressHere')}
          className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('checkout.addressTip')}
        </p>
      </div>

      {/* Map */}
      <div className="mb-4">
        <div className="mb-2 text-sm text-gray-600 flex items-center">
          <MapPin className="h-4 w-4 mr-1" />
          {t('checkout.clickMapTip')}
        </div>
        <div ref={mapRef} className="w-full h-52 sm:h-64 rounded-lg border border-gray-300 cursor-crosshair"></div>
      </div>

      {/* Distance and Delivery Info */}
      {distance !== null && (
        <div className={`p-3 rounded-lg border ${
          deliveryAvailable 
            ? 'bg-blue-50 border-blue-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <div className={`font-semibold ${
                deliveryAvailable ? 'text-blue-800' : 'text-red-800'
              }`}>
                {t('checkout.distance', { dist: distance.toFixed(1) })}
              </div>
              <div className={`text-sm ${
                deliveryAvailable ? 'text-blue-600' : 'text-red-600'
              }`}>
                {deliveryAvailable
                  ? t('checkout.deliveryCharge', { charge: deliveryCharge })
                  : t('checkout.deliveryNotAvailable')
                }
              </div>
              {nearestBranch && (
                <div className="text-xs text-gray-600 mt-1">
                  {t('checkout.nearestBranchLabel', { name: nearestBranch.name })}
                </div>
              )}
              {!deliveryAvailable && (
                <div className="text-xs text-red-600 mt-1">
                  {t('checkout.within20km')}
                </div>
              )}
            </div>
            <Navigation className={`h-5 w-5 ${
              deliveryAvailable ? 'text-blue-600' : 'text-red-600'
            }`} />
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-800 text-sm">{error}</div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-blue-800 text-sm">Calculating distance...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMapsDistance;